import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { GlassCard } from '@/components/brand/GlassCard';
import { adminOverview, adminListConsultations, adminListPayments } from '@/lib/api';
import { formatGBP, formatDate } from '@/lib/utils';
import { toast } from 'sonner';

// --- Helpers ----------------------------------------------------------------

const consultationBadge = (s) => {
    if (s === 'new') return 'border-la-gold/40 text-la-gold bg-la-gold/10';
    if (s === 'in_progress') return 'border-amber-300/30 text-amber-200 bg-amber-200/10';
    if (s === 'closed') return 'border-emerald-400/30 text-emerald-200 bg-emerald-200/10';
    return 'border-white/10 text-la-text2 bg-white/[0.04]';
};

const paymentBadge = (s) => {
    if (s === 'paid') return 'border-emerald-400/30 text-emerald-200 bg-emerald-200/10';
    if (s === 'failed' || s === 'expired') return 'border-rose-400/30 text-rose-200 bg-rose-200/10';
    return 'border-amber-300/30 text-amber-200 bg-amber-200/10';
};

// --- Sub-components ---------------------------------------------------------

const Stat = ({ label, value }) => (
    <GlassCard className="p-5">
        <div className="text-[11px] tracking-[0.16em] uppercase text-la-text3">{label}</div>
        <div className="mt-2 font-display text-3xl text-la-text">{value ?? '—'}</div>
    </GlassCard>
);

const StatGrid = ({ stats }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Stat label="New consultations" value={stats?.consultations_new} />
        <Stat label="Total consultations" value={stats?.consultations_total} />
        <Stat label="Paid revenue" value={formatGBP(stats?.total_revenue_gbp || 0)} />
        <Stat label="Pending payments" value={stats?.payments_pending} />
    </div>
);

const RecentConsultations = ({ items }) => (
    <GlassCard className="p-5">
        <div className="flex items-center justify-between">
            <h3 className="font-display text-xl text-la-text">Recent consultations</h3>
            <span className="text-[11px] tracking-[0.16em] uppercase text-la-text3">Last 6</span>
        </div>
        <ul className="mt-4 divide-y divide-white/10">
            {items.length === 0 ? <li className="py-6 text-sm text-la-text3">No consultations yet.</li> : items.map((c) => (
                <li key={c.consultation_id} className="py-3 flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm text-la-text">{c.name} <span className="text-la-text3">· {c.category}</span></div>
                        <div className="text-[11px] text-la-text3">{formatDate(c.created_at)} · {c.urgency}</div>
                    </div>
                    <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded-la-sm border ${consultationBadge(c.status)}`}>{c.status}</span>
                </li>
            ))}
        </ul>
    </GlassCard>
);

const RecentPayments = ({ items }) => (
    <GlassCard className="p-5">
        <div className="flex items-center justify-between">
            <h3 className="font-display text-xl text-la-text">Recent payments</h3>
            <span className="text-[11px] tracking-[0.16em] uppercase text-la-text3">Last 6</span>
        </div>
        <ul className="mt-4 divide-y divide-white/10">
            {items.length === 0 ? <li className="py-6 text-sm text-la-text3">No payments yet.</li> : items.map((p) => (
                <li key={p.session_id} className="py-3 flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm text-la-text">{p.metadata?.client_name || p.metadata?.client_email || 'Client'} <span className="text-la-text3">· {formatGBP(p.amount)}</span></div>
                        <div className="text-[11px] text-la-text3">{p.metadata?.payment_reference || p.metadata?.reference || '—'} · {formatDate(p.created_at)}</div>
                    </div>
                    <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded-la-sm border ${paymentBadge(p.payment_status)}`}>{p.payment_status || 'pending'}</span>
                </li>
            ))}
        </ul>
    </GlassCard>
);

// --- Main page --------------------------------------------------------------

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [recentConsults, setRecentConsults] = useState([]);
    const [recentPayments, setRecentPayments] = useState([]);

    const load = useCallback(async () => {
        try {
            const [statsData, consultations, payments] = await Promise.all([
                adminOverview(),
                adminListConsultations({ limit: 6 }),
                adminListPayments(),
            ]);
            setStats(statsData);
            setRecentConsults(consultations);
            setRecentPayments((payments || []).slice(0, 6));
        } catch (err) {
            toast.error('Could not load admin overview.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <AdminLayout title="Overview">
            <StatGrid stats={stats} />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <RecentConsultations items={recentConsults} />
                <RecentPayments items={recentPayments} />
            </div>
        </AdminLayout>
    );
}
